/**
 * Copyright (c) 2019 2020 2021 2022 AIGIA. All Rights Reserved.
 * see license.txt
 */

"use strict";

import {Callback, IAccountPublic, IErrorObject, IQueryOption} from "../../../../types/platform/universe";

import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {retry} from "rxjs/operators";

// import * as NodeRSA from "node-rsa";
import {environment} from '../../../environments/environment';

import {PublicKeyService} from "../../platform/base/services/publickey.service";
import {QueryableService} from "../../platform/base/services/queryable.service";
import {Errors} from "../base/library/errors";

@Injectable({
	providedIn: "root",
})

export class GuestsService extends QueryableService {

	/**
	 *
	 * @param http
	 * @param PublicKey
	 */
	constructor(
		public http: HttpClient,
		private PublicKey: PublicKeyService
	) {
		super(http, "guests");
	}

	/**
	 * 公開鍵暗号
	 *
	 * @param key 公開鍵
	 * @param plain 原文
	 * @param callback 暗号を返すコールバック
	 */
	private static publickey_encrypt(key: string, plain: string, callback: Callback<any>): void {
		try {
			// 	const rsa = new NodeRSA(key, "pkcs1-public-pem", {encryptionScheme: "pkcs1_oaep"});
			// 	callback(null, rsa.encrypt(plain, "base64"));
			callback(null, plain);
		} catch (error: any) {
			callback(error, "");
		}
	}

	/**
	 * 公開鍵暗号
	 *
	 * @param key 公開鍵
	 * @param plain 原文
	 * @param callback 暗号を返すコールバック
	 */
	private value_encrypt(key: string, plain: object, callback: Callback<any>) {
		try {
			const use_publickey = environment.use_publickey;
			if (use_publickey) {
				GuestsService.publickey_encrypt(key, JSON.stringify(plain), (error, encryptedText): void => {
					if (!error) {
						callback(null, encryptedText);
					} else {
						callback(error, "");
					}
				});
			} else {
				callback(null, JSON.stringify(plain));
			}
		} catch (error: any) {
			callback(error, "");
		}
	}

	/**
	 * 単一のオブジェクトを返す
	 *
	 * @param user_id オブジェクトID
	 * @param callback オブジェクトを返すコールバック
	 */
	public get_user(user_id: string, callback: Callback<IAccountPublic>): void {
		let target_url: string = this.endPoint + "/" + this.model + "/auth";
		if (user_id) {
			target_url += "/" + encodeURIComponent(user_id);
		}
		this.http.get(target_url, this.httpOptions).pipe(retry(3)).subscribe(
			{
				next: (result: any): void => {
					if (this.isNumber(result.code)) {
						if (result.code === 0) {
							callback(null, result.value);
						} else {
							callback(Errors.serverError(result, "A00374"), null);
						}
					} else {
						callback(Errors.httpError(500, "A00359"), null);
					}
				},
				error: (error: HttpErrorResponse): void => {
					callback(Errors.socketError(error, "A00360"), null);
				},
				complete: () => {
				}
			}
		);
	}

	/**
	 * 単一のオブジェクトを返す
	 * /guests/auth/from_name/:username
	 * @param username
	 * @param callback オブジェクトを返すコールバック
	 */
	public get_user_by_name(username: string, callback: Callback<object>): void {
		const target_url = this.endPoint + "/" + this.model + "/auth/from_name/" + encodeURIComponent(username);
		this.http.get(target_url, this.httpOptions).pipe(retry(3)).subscribe(
			{
				next: (result: any): void => {
					if (this.isNumber(result.code)) {
						if (result.code === 0) {
							callback(null, result.value);
						} else {
							callback(Errors.serverError(result, "A00374"), null);
						}
					} else {
						callback(Errors.httpError(500, "A00359"), null);
					}
				},
				error: (error: HttpErrorResponse): void => {
					callback(Errors.socketError(error, "A00360"), null);
				},
				complete: () => {
				}
			}
		);
	}

	/**
	 * @param user_id
	 * @param content
	 * @param callback
	 */
	public put_user(user_id: string, content: any, callback: Callback<any>): void {
		let target_url: string = this.endPoint + "/" + this.model + "/auth";
		if (user_id) {
			target_url += "/" + encodeURIComponent(user_id);
		}
		this.http.put(target_url, content, this.httpOptions).pipe(retry(3)).subscribe(
			{
				next: (result: any): void => {
					if (this.isNumber(result.code)) {
						if (result.code === 0) {
							callback(null, result);
						} else {
							callback(Errors.serverError(result, "A00374"), null);
						}
					} else {
						callback(Errors.httpError(500, "A00360"), null);
					}
				},
				error: (error: HttpErrorResponse): void => {
					callback(Errors.socketError(error, "A00361"), null);
				},
				complete: () => {
				}
			}
		);
	}

	/**
	 *
	 * @param id
	 * @param callback
	 */
	public delete(id: string, callback: Callback<any>): void {
		this.http.delete(this.endPoint + "/" + this.model + "/auth/" + encodeURIComponent(id), this.httpOptions).pipe(retry(3)).subscribe(
			{
				next: (result: any): void => {
					if (this.isNumber(result.code)) {
						if (result.code === 0) {
							callback(null, result.value);
						} else {
							callback(Errors.serverError(result, "A00374"), null);
						}
					} else {
						callback(Errors.httpError(500, "A00362"), null);
					}
				},
				error: (error: HttpErrorResponse): void => {
					callback(Errors.socketError(error, "A00363"), null);
				},
				complete: () => {
				}
			}
		);
	}

	/**
	 *
	 * @param user_id
	 * @param callback
	 */
	public is_2fa(user_id: string, callback: Callback<any>): void {
		this.http.get(this.endPoint + "/" + this.model + "/auth/is2fa/" + encodeURIComponent(user_id), this.httpOptions).pipe(retry(3)).subscribe(
			{
				next: (result: any): void => {
					if (this.isNumber(result.code)) {
						if (result.code === 0) {
							callback(null, result.value.is_2fa);
						} else {
							callback(Errors.serverError(result, "A00374"), null);
						}
					} else {
						callback(Errors.httpError(500, "A00364"), null);
					}
				},
				error: (error: HttpErrorResponse): void => {
					callback(Errors.socketError(error, "A00365"), null);
				},
				complete: () => {
				}
			}
		);
	}

	/**
	 *
	 * @param user_id
	 * @param callback
	 */
	public set_2fa(user_id: string, callback: Callback<any>): void {
		this.http.post(this.endPoint + "/" + this.model + "/auth/set2fa/" + encodeURIComponent(user_id), {}, this.httpOptions).pipe(retry(3)).subscribe(
			{
				next: (result: any): void => {
					if (this.isNumber(result.code)) {
						if (result.code === 0) {
							callback(null, result.value.qrcode);
						} else {
							callback(Errors.serverError(result, "A00374"), null);
						}
					} else {
						callback(Errors.httpError(500, "A00366"), null);
					}
				},
				error: (error: HttpErrorResponse): void => {
					callback(Errors.socketError(error, "A00367"), null);
				},
				complete: () => {
				}
			}
		);
	}

	/**
	 *
	 * @param user_id
	 * @param callback
	 */
	public reset_2fa(user_id: string, callback: Callback<any>): void {
		this.http.post(this.endPoint + "/" + this.model + "/auth/reset2fa/" + encodeURIComponent(user_id), {}, this.httpOptions).pipe(retry(3)).subscribe(
			{
				next: (result: any): void => {
					if (this.isNumber(result.code)) {
						if (result.code === 0) {
							callback(null, result.value);
						} else {
							callback(Errors.serverError(result, "A00374"), null);
						}
					} else {
						callback(Errors.httpError(500, "A00368"), null);
					}
				},
				error: (error: HttpErrorResponse): void => {
					callback(Errors.socketError(error, "A00369"), null);
				},
				complete: () => {
				}
			}
		);
	}

	/*
	*
	*/
	public password_immediate(user_id: string, password: string, callback: Callback<any>): void {
		this.PublicKey.fixed((error: IErrorObject, key: string): void => {
			if (!error) {
				this.value_encrypt(key, {user_id, password}, (error: IErrorObject, value: any): void => {
					if (!error) {
						this.http.post(this.endPoint + "/auth/immediate/password", {content: value}, this.httpOptions).pipe(retry(3)).subscribe(
							{
								next: (result: any): void => {
									if (this.isNumber(result.code)) {
										if (result.code === 0) {
											callback(null, result.value);
										} else {
											callback(Errors.serverError(result, "A00374"), null);
										}
									} else {
										callback(Errors.httpError(500, "A00370"), null);
									}
								},
								error: (error: HttpErrorResponse): void => {
									callback(Errors.socketError(error, "A00371"), null);
								},
								complete: () => {
								}
							}
						);
					} else {
						callback(Errors.generalError(error.code, error.message, "A00174"), null);
					}
				});
			} else {
				callback(Errors.generalError(error.code, error.message, "A00174"), null);
			}
		});
	}

	/**
	 * yーザ名更新
	 * メール存在確認なし
	 *
	 * @param user_id ユーザID
	 * @param update_username ユーザ名
	 * @param callback コールバック
	 */
	public username_immediate(user_id: string, update_username: string, callback: Callback<any>): void {
		this.PublicKey.fixed((error: IErrorObject, key: string): void => {
			if (!error) {
				this.value_encrypt(key, {user_id, update_username}, (error: IErrorObject, value: any): void => {
					if (!error) {
						this.http.post(this.endPoint + "/auth/immediate/username", {content: value}, this.httpOptions).pipe(retry(3)).subscribe(
							{
								next: (result: any): void => {
									if (this.isNumber(result.code)) {
										if (result.code === 0) {
											callback(null, result.value);
										} else {
											callback(Errors.generalError(result.code, result.message, "A00169"), null);
										}
									} else {
										callback(Errors.httpError(500, "A00170"), null);
									}
								},
								error: (error: HttpErrorResponse): void => {
									callback(Errors.socketError(error, "A00171"), null);
								},
								complete: () => {
								}
							}
						);
					} else {
						callback(Errors.generalError(error.code, error.message, "A01916"), null);
					}
				});
			} else {
				callback(Errors.generalError(error.code, error.message, "A02016"), null);
			}
		});
	}

	/*
	* relation to
	* */
	public make_relation(user_id: string, to_id: string, type: string, callback: any): void {
		let target_url: string = this.endPoint + '/' + this.model + '/relation';
		if (user_id) {
			target_url += "/" + encodeURIComponent(user_id);
		}
		this.http.post(target_url, {to: to_id, type: type}, this.httpOptions).pipe(retry(3)).subscribe(
			{
				next: (result: any): void => {
					if (this.isNumber(result.code)) {
						if (result.code === 0) {
							callback(null, result.value);
						} else {
							callback(Errors.serverError(result, "A00338"), null);
						}
					} else {
						callback(Errors.httpError(500, "A00339"), null);
					}
				},
				error: (error: HttpErrorResponse): void => {
					callback(Errors.socketError(error, "A00336"), null);
				},
				complete: () => {
				}
			}
		);
	}

	/*
	* relation to
	* */
	public make_relation_to(from_id: string, to_id: string, type: string, callback: Callback<any>): void {
		if (from_id) {
			if (to_id) {
				if (type) {
					this.http.post(this.endPoint + '/' + this.model + '/relation/to', {from: from_id, to: to_id, type: type}, this.httpOptions).pipe(retry(3)).subscribe(
						{
							next: (result: any): void => {
								if (this.isNumber(result.code)) {
									if (result.code === 0) {
										callback(null, result.value);
									} else {
										callback(Errors.serverError(result, "A00138"), null);
									}
								} else {
									callback(Errors.httpError(500, "A00139"), null);
								}
							},
							error: (error: HttpErrorResponse): void => {
								callback(Errors.socketError(error, "A00140"), null);
							},
							complete: () => {
							}
						}
					);
				} else {
					callback(Errors.generalError(-1, "'type' required.", "A00174"), null);
				}
			} else {
				callback(Errors.generalError(-1, "'to' required.", "A00175"), null);
			}
		} else {
			callback(Errors.generalError(-1, "'from' required.", "A00176"), null);
		}
	}

	/*
	* relation先一覧
	* */
	public relation_to(user_id: string, type: string, option: any, callback: Callback<any>): void {
		if (type) {
			if (option) {
				let target_url: string = this.endPoint + "/" + this.model + "/relation/to/";
				if (user_id) {
					target_url += encodeURIComponent(user_id) + "/" + encodeURIComponent(type) + "/" + encodeURIComponent(JSON.stringify(option));
				} else {
					target_url += encodeURIComponent(type) + "/" + encodeURIComponent(JSON.stringify(option));
				}
				this.http.get(target_url, this.httpOptions).pipe(retry(3)).subscribe(
					{
						next: (results: any): void => {
							if (Array.isArray(results)) {
								//	const filtered: any[] = results.filter((result: any) => {return result.account;});
								callback(null, results); // todo
							} else {
								callback(Errors.networkError("A00179"), null);
							}
						},
						error: (error: HttpErrorResponse): void => {
							callback(Errors.networkException(error, "A00144"), null);
						},
						complete: () => {
						}
					}
				);
			} else {
				callback(Errors.generalError(-1, "'option' required.", "A00180"), null);
			}
		} else {
			callback(Errors.generalError(-1, "'type' required.", "A00181"), null);
		}
	}

	/*
	* relation元一覧
	* */
	public all_relation(type: string, linktype: string, option: any, callback: Callback<any>): void {
		if (type) {
			if (option) {
				this.http.get(this.endPoint + "/" + this.model + "/relation/all/" + encodeURIComponent(type) + "/" + encodeURIComponent(linktype) + "/" + encodeURIComponent(JSON.stringify(option)), this.httpOptions).pipe(retry(3)).subscribe(
					{
						next: (results: any): void => {
							if (Array.isArray(results)) {
								callback(null, results); // todo
							} else {
								callback(Errors.networkError("A00141"), null);
							}
						},
						error: (error: HttpErrorResponse): void => {
							callback(Errors.socketError(error, "A00142"), null);
						},
						complete: () => {
						}
					}
				);
			} else {
				callback(Errors.generalError(-1, "'option' required.", "A00182"), null);
			}
		} else {
			callback(Errors.generalError(-1, "'type' required.", "A00183"), null);
		}
	}

	/*
	* relation元一覧
	* */
	public relation_from_user(user_id: string, linktype: string, option: any, callback: Callback<any>): void {
		if (user_id) {
			if (linktype) {
				if (option) {
					this.http.get(this.endPoint + "/" + this.model + "/relation/fromuser/" + encodeURIComponent(user_id) + "/" + encodeURIComponent(linktype) + "/" + encodeURIComponent(JSON.stringify(option)), this.httpOptions).pipe(retry(3)).subscribe(
						{
							next: (results: any): void => {
								if (Array.isArray(results)) {
									//	const filtered: any[] = results.filter((result: any) => {return result.account;});
									callback(null, results); // todo
								} else {
									callback(Errors.networkError("A00141"), null);
								}
							},
							error: (error: HttpErrorResponse): void => {
								callback(Errors.socketError(error, "A00142"), null);
							},
							complete: () => {
							}
						}
					);
				} else {
					callback(Errors.generalError(-1, "'option' required.", "A00182"), null);
				}
			} else {
				callback(Errors.generalError(-1, "'type' required.", "A00183"), null);
			}
		} else {
			callback(Errors.generalError(-1, "'username' required.", "A00184"), null);
		}
	}


	/*
	* relation元一覧
	* */
	public relation_from_user_with_type(user_id: string, type: string, linktype: string, option: any, callback: Callback<any>): void {
		if (user_id) {
			if (linktype) {
				if (option) {
					this.http.get(this.endPoint + "/" + this.model + "/relation/fromusertype/" + encodeURIComponent(user_id) + "/" + encodeURIComponent(type) + "/" + encodeURIComponent(linktype) + "/" + encodeURIComponent(JSON.stringify(option)), this.httpOptions).pipe(retry(3)).subscribe(
						{
							next: (results: any): void => {
								if (results) {
									//	const filtered: any[] = results.filter((result: any) => {return result.account;});
									callback(null, results); // todo
								} else {
									callback(Errors.networkError("A00141"), null);
								}
							},
							error: (error: HttpErrorResponse): void => {
								callback(Errors.socketError(error, "A00142"), null);
							},
							complete: () => {
							}
						}
					);
				} else {
					callback(Errors.generalError(-1, "'option' required.", "A00182"), null);
				}
			} else {
				callback(Errors.generalError(-1, "'type' required.", "A00183"), null);
			}
		} else {
			callback(Errors.generalError(-1, "'username' required.", "A00184"), null);
		}
	}


	/*
	* relation先一覧
	* */
	public relation_to_user(user_id: string, type: string, option: any, callback: Callback<any>): void {
		if (user_id) {
			if (type) {
				if (option) {
					this.http.get(this.endPoint + "/" + this.model + "/relation/touser/" + encodeURIComponent(user_id) + "/" + encodeURIComponent(type) + "/" + encodeURIComponent(JSON.stringify(option)), this.httpOptions).pipe(retry(3)).subscribe(
						{
							next: (results: any): void => {
								if (results) {
									//	const filtered: any[] = results.filter((result: any) => {return result.account;});
									callback(null, results); // todo
								} else {
									callback(Errors.networkError("A00185"), null);
								}
							},
							error: (error: HttpErrorResponse): void => {
								callback(Errors.networkException(error, "A00144"), null);
							},
							complete: () => {
							}
						}
					);
				} else {
					callback(Errors.generalError(-1, "'option' required.", "A00186"), null);
				}
			} else {
				callback(Errors.generalError(-1, "'type' required.", "A00187"), null);
			}
		} else {
			callback(Errors.generalError(-1, "'username' required.", "A00188"), null);
		}
	}

	/*
	* relation to
	* */
	public break_relation(user_id: string, to_id: string, type: string, callback: any): void {
		let target_url: string = this.endPoint + "/" + this.model + "/relation/remove/";
		if (user_id) {
			target_url += encodeURIComponent(user_id) + "/" + encodeURIComponent(to_id) + "/" + encodeURIComponent(type);
		} else {
			target_url += encodeURIComponent(to_id) + "/" + encodeURIComponent(type);
		}
		this.http.delete(target_url, this.httpOptions).pipe(retry(3)).subscribe(
			{
				next: (result: any): void => {
					if (this.isNumber(result.code)) {
						if (result.code === 0) {
							callback(null, result.value);
						} else {
							callback(Errors.serverError(result, "A00338"), null);
						}
					} else {
						callback(Errors.httpError(500, "A00339"), null);
					}
				},
				error: (error: HttpErrorResponse): void => {
					callback(Errors.socketError(error, "A00338"), null);
				},
				complete: () => {
				}
			}
		);
	}

	/*
	* relation to
	* */
	public belongs(user_id: string, callback: any): void {
		let target_url: string = this.endPoint + '/' + this.model + '/belongs';
		if (user_id) {
			target_url += "/" + encodeURIComponent(user_id);
		}
		this.http.get(target_url, this.httpOptions).pipe(retry(3)).subscribe(
			{
				next: (result: any): void => {
					if (this.isNumber(result.code)) {
						if (result.code === 0) {
							callback(null, result.value);
						} else {
							callback(result, null);
						}
					} else {
						callback(Errors.httpError(500, "A00221"), null);
					}
				},
				error: (error: HttpErrorResponse): void => {
					callback(Errors.socketError(error, "A00336"), null);
				},
				complete: () => {
				}
			}
		);
	}

	/**
	 * @param user_id
	 * @param content
	 * @param callback
	 */
	public fix_parent(user_id: string, callback: Callback<any>): void {
		let target_url: string = this.endPoint + "/" + this.model + "/fix";
		if (user_id) {
			target_url += "/" + encodeURIComponent(user_id);
		}
		this.http.put(target_url, {}, this.httpOptions).pipe(retry(3)).subscribe(
			{
				next: (result: any): void => {
					if (this.isNumber(result.code)) {
						if (result.code === 0) {
							callback(null, result);
						} else {
							callback(Errors.serverError(result, "A00374"), null);
						}
					} else {
						callback(Errors.httpError(500, "A00360"), null);
					}
				},
				error: (error: HttpErrorResponse): void => {
					callback(Errors.socketError(error, "A00361"), null);
				},
				complete: () => {
				}
			}
		);
	}


	/**
	 * レポート
	 *
	 * @param query MongoDBのクエリーオブジェクト
	 * @param option MongoDBのオプションオブジェクト
	 * @param callback 結果配列を返すコールバック
	 * /guests/auth/report/:query/:option
	 */
	public report(query: object, option: IQueryOption, callback: Callback<object[]>): void {
		this.Encode(query, (error: IErrorObject, queryString: string): void => {
			if (!error) {
				this.Encode(option, (error: IErrorObject, optionString: string): void => {
					if (!error) {
						this.http.get(this.endPoint + "/" + this.model + "/auth/report/" + queryString + "/" + optionString, this.httpOptions).pipe(retry(3)).subscribe(
							{
								next: (result: any): void => {
									if (this.isNumber(result.code)) {
										if (result.code === 0) {
											callback(null, result.value);
										} else {
											callback(Errors.serverError(result, "A00221"), null);
										}
									} else {
										callback(Errors.httpError(500, "A00222"), null);
									}
								},
								error: (error: HttpErrorResponse): void => {
									callback(Errors.socketError(error, "A00185"), []);
								},
								complete: () => {
								}
							}
						);
					} else {
						callback(Errors.responseError("A00186"), []);
					}
				});
			} else {
				callback(Errors.responseError("A00187"), []);
			}
		});
	}

}
